import axios, { AxiosInstance, AxiosError } from 'axios'
import type {
  AssetResponse,
  AssetListResponse,
  TemplateResponse,
  TemplateWithLayersResponse,
  CanvasPresetsResponse,
  ArtJobResponse,
  ArtJobListResponse,
  ConjuntoResponse,
  ConjuntoVersionResponse,
  ExportResponse,
  LoginResponse,
  LoginRequest,
  CreateTemplateRequest,
  CreateLayerRequest,
  CreateArtJobRequest,
  CreateConjuntoRequest,
  UpdateConjuntoRequest,
  TextStyleResponse,
  CreateTextStyleRequest,
  LayerResponse,
  LuckyVariationsResponse,
  ArtImageResponse,
} from './api-types'

class APIClient {
  private client: AxiosInstance
  private token: string | null = null
  private readonly tokenKey: string

  constructor(baseURL: string) {
    this.tokenKey =
      typeof window !== 'undefined'
        ? (import.meta.env?.VITE_AUTH_TOKEN_KEY) || 'auth_token'
        : 'auth_token'

    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 600000, // 600 segundos (10 min) - Fotos grandes em base64 demoram mais
    })

    // Interceptador de requisição - adiciona token
    this.client.interceptors.request.use(
      (config) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Interceptador de resposta - trata erros
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Token inválido ou expirado
          this.clearToken()
          if (typeof window !== 'undefined') {
            window.location.href = '/login'
          }
        }
        return Promise.reject(error)
      }
    )

    // Carrega token do localStorage (apenas no browser)
    if (typeof window !== 'undefined') {
      const savedToken = localStorage.getItem(this.tokenKey)
      if (savedToken) {
        this.token = savedToken
      }
    }
  }

  // ===== TOKEN MANAGEMENT =====

  setToken(token: string) {
    this.token = token
    if (typeof window !== 'undefined') {
      localStorage.setItem(this.tokenKey, token)
    }
  }

  getToken(): string | null {
    return this.token
  }

  clearToken() {
    this.token = null
    if (typeof window !== 'undefined') {
      localStorage.removeItem(this.tokenKey)
    }
  }

  isAuthenticated(): boolean {
    return !!this.token
  }

  // ===== AUTH =====

  async login(data: LoginRequest) {
    const response = await this.client.post<LoginResponse>('/auth/login', data)
    if (response.data.access_token) {
      this.setToken(response.data.access_token)
      // Salvar email no localStorage para exibir no toast de boas-vindas
      if (typeof window !== 'undefined') {
        localStorage.setItem('userEmail', data.email)
      }
    }
    return response
  }

  async logout() {
    this.clearToken()
    // Remover email ao fazer logout
    if (typeof window !== 'undefined') {
      localStorage.removeItem('userEmail')
    }
  }

  // ===== ASSETS =====

  async uploadAsset(file: File) {
    const formData = new FormData()
    formData.append('file', file)
    return this.client.post<AssetResponse>('/api/v1/assets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  async listAssets(page: number = 1, limit: number = 50, query?: string) {
    return this.client.get<AssetListResponse>('/api/v1/assets', {
      params: { page, limit, query },
    })
  }

  async getAsset(id: string) {
    return this.client.get<AssetResponse>(`/api/v1/assets/${id}`)
  }

  async deleteAsset(id: string) {
    return this.client.delete(`/api/v1/assets/${id}`)
  }

  // ===== TEMPLATES =====

  async createTemplate(data: CreateTemplateRequest) {
    return this.client.post<TemplateResponse>('/api/v1/templates', data)
  }

  async listTemplates(skip: number = 0, limit: number = 100) {
    return this.client.get<TemplateResponse[]>('/api/v1/templates', {
      params: { skip, limit },
    })
  }

  async getTemplate(id: string) {
    return this.client.get<TemplateWithLayersResponse>(`/api/v1/templates/${id}`)
  }

  async getCanvasPresets() {
    return this.client.get<CanvasPresetsResponse>('/api/v1/templates/presets/canvas-sizes')
  }

  async addLayer(templateId: string, layerData: CreateLayerRequest) {
    return this.client.post<LayerResponse>(`/api/v1/templates/${templateId}/layers`, layerData)
  }

  async deleteLayer(templateId: string, layerId: string) {
    return this.client.delete(`/api/v1/templates/${templateId}/layers/${layerId}`)
  }

  async deleteAllLayers(templateId: string) {
    return this.client.delete(`/api/v1/templates/${templateId}/layers`)
  }

  async deleteTemplate(id: string) {
    return this.client.delete(`/api/v1/templates/${id}`)
  }

  async updateTemplate(id: string, data: { name: string }) {
    return this.client.put<TemplateResponse>(`/api/v1/templates/${id}`, data)
  }

  // ===== TEXT STYLES =====

  async createTextStyle(data: CreateTextStyleRequest) {
    return this.client.post<TextStyleResponse>('/api/v1/text-styles', data)
  }

  async listTextStyles() {
    return this.client.get<TextStyleResponse[]>('/api/v1/text-styles')
  }

  async getTextStyle(id: string) {
    return this.client.get<TextStyleResponse>(`/api/v1/text-styles/${id}`)
  }

  // ===== ART JOBS =====

  async createArtJob(data: CreateArtJobRequest) {
    return this.client.post<ArtJobResponse>('/api/v1/art-jobs', {
      ...data,
      model: data.model || 'gemini-2.5-flash-image-preview',
    })
  }

  async getArtJob(id: string) {
    return this.client.get<ArtJobResponse>(`/api/v1/art-jobs/${id}`)
  }

  async listArtJobs(page: number = 1, limit: number = 50) {
    return this.client.get<ArtJobListResponse>('/api/v1/art-jobs', {
      params: { page, limit },
    })
  }

  async deleteArtJob(id: string) {
    return this.client.delete(`/api/v1/art-jobs/${id}`)
  }

  async regenerateArtJob(id: string) {
    return this.client.post<ArtJobResponse>(`/api/v1/art-jobs/regenerate/${id}`)
  }

  async editArtJob(id: string, editPrompt: string) {
    return this.client.post<ArtJobResponse>(`/api/v1/art-jobs/edit/${id}`, {
      edit_prompt: editPrompt,
    })
  }

  async rerollArtJob(id: string, variationPrompt?: string) {
    return this.client.post<ArtJobResponse>(`/api/v1/art-jobs/reroll/${id}`, {
      variation_prompt: variationPrompt || null,
    })
  }

  async listLuckyVariations() {
    return this.client.get<LuckyVariationsResponse>('/api/v1/art-jobs/lucky/variations')
  }

  async regenerateLuckyVariation(jobId: string, variationName: string) {
    return this.client.post<ArtJobResponse>(
      `/api/v1/art-jobs/${jobId}/regenerate-variation/${encodeURIComponent(variationName)}`
    )
  }

  async listArtJobImages(jobId: string) {
    return this.client.get<ArtImageResponse[]>(`/api/v1/art-jobs/${jobId}/images`)
  }

  // ===== CONJUNTOS =====

  async createConjunto(data: CreateConjuntoRequest) {
    return this.client.post<ConjuntoResponse>('/api/v1/conjuntos', data)
  }

  async listConjuntos(skip: number = 0, limit: number = 100) {
    return this.client.get<ConjuntoResponse[]>('/api/v1/conjuntos', {
      params: { skip, limit },
    })
  }

  async getConjunto(id: string) {
    return this.client.get<ConjuntoResponse>(`/api/v1/conjuntos/${id}`)
  }

  async updateConjunto(id: string, data: UpdateConjuntoRequest) {
    return this.client.patch<ConjuntoResponse>(`/api/v1/conjuntos/${id}`, data)
  }

  async exportConjunto(id: string) {
    return this.client.post<ExportResponse>(`/api/v1/conjuntos/${id}/export`)
  }

  async listVersions(conjuntoId: string) {
    return this.client.get<ConjuntoVersionResponse[]>(
      `/api/v1/conjuntos/${conjuntoId}/versions`
    )
  }

  async getExport(id: string) {
    return this.client.get<ExportResponse>(`/api/v1/conjuntos/exports/${id}`)
  }

  async downloadFile(s3Url: string) {
    // Send the S3 URL to the backend which will fetch and return it as a file
    return this.client.get(`/api/v1/files/download`, {
      params: { url: s3Url },
      responseType: 'blob', // Important: tell axios to return a blob
    })
  }

  // ===== VISION/AI =====

  async describeImage(image: string, context: string = 'key_visual') {
    return this.client.post(`/api/v1/art-jobs/vision/describe`, {
      image,
      context,
    })
  }
}

// Função para obter o baseURL correto
function getBaseURL(): string {
  if (typeof window === 'undefined') {
    return 'http://localhost:8075'
  }

  // Se houver VITE_API_URL definido, usar
  if (import.meta.env?.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }

  // Caso contrário, usar o hostname atual + :8075 (porta do backend no docker-compose)
  // Isso funciona tanto em localhost quanto em IP addresses
  const protocol = window.location.protocol
  const hostname = window.location.hostname
  const baseURL = `${protocol}//${hostname}:8075`
  
  console.log('[API_CLIENT] Auto-detected baseURL:', baseURL)
  return baseURL
}

// Singleton instance
export const apiClient = new APIClient(getBaseURL())

// Export types for convenience
export type { APIClient }



